// File: src/hooks/usePressAndHold.js

import { useRef, useCallback } from 'react';

/**
 * Custom hook for detecting desktop hover, mobile long-press, and quick clicks.
 * 
 * @param {Function} onLongPress - Fired when hovered or long-pressed
 * @param {Function} onClick - Fired on quick tap/click
 * @param {number} delay - Delay in ms before triggering popover (default 250ms)
 */
const usePressAndHold = (onLongPress, onClick, delay = 250) => {
    const timerRef = useRef(null);
    const isLongPress = useRef(false);

    // Start timer on mouse enter or touch start
    const start = useCallback((e) => {
        isLongPress.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            isLongPress.current = true;
            if (onLongPress) onLongPress(e);
        }, delay);
    }, [onLongPress, delay]);

    // End gesture on mouse up / touch end
    const stop = useCallback((e) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        // If it wasn't a long-press/hover trigger, execute normal quick click
        if (!isLongPress.current && onClick) {
            onClick(e);
        }
    }, [onClick]);

    // Cancel timer when leaving element
    const cancel = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, []);

    return {
        onMouseDown: start,
        onMouseUp: stop,
        onTouchStart: start,
        onTouchEnd: stop,
        onMouseEnter: start,   // 🌟 Desktop hover trigger
        onMouseLeave: cancel,  // 🌟 Cancel timer if cursor leaves before delay
    };
};

export default usePressAndHold;